export type NavItem = { label: string; href: string };

export const navItems: NavItem[] = [
  { label: "Home", href: "/" },
  { label: "Features", href: "/features" },
  { label: "Install", href: "/install" },
  { label: "Docs", href: "/docs" },
  { label: "Blog", href: "/blog" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
];

export const legalNavItems: NavItem[] = [
  { label: "Privacy", href: "/privacy" },
  { label: "Terms", href: "/terms" },
  { label: "Accessibility", href: "/accessibility" },
];

export type BlogPost = { slug: string; title: string; description: string; date: string };

export const blogPosts: BlogPost[] = [
  {
    slug: "why-your-team-needs-real-workforce-software",
    title: "Why Your Team Needs Real Workforce Software (Not WhatsApp Groups and Spreadsheets)",
    description:
      "A WhatsApp group and a shared spreadsheet work fine at five people. What breaks first as a team grows, and what a guarded assignment pipeline and an explicit attendance state machine fix that ad hoc tools structurally can't.",
    date: "2026-08-13",
  },
  {
    slug: "zulivio-is-actually-free",
    title: "Zulivio Is Actually Free — Here's What AGPL-3.0 and Self-Hosted Really Mean",
    description:
      "No subscription, no per-seat fee, no premium tier. What \"free and open source\" actually guarantees under AGPL-3.0, what it doesn't, and where the real cost of running Zulivio goes instead.",
    date: "2026-08-13",
  },
  {
    slug: "how-role-based-access-control-and-assignments-work",
    title: "Inside Zulivio: How Role-Based Access Control and Guarded Work Assignments Actually Work",
    description:
      "A five-level role hierarchy enforced on every request, not just hidden buttons — and a work-assignment pipeline that rejects invalid status jumps instead of trusting the client. How it's built, and how it's tested.",
    date: "2026-08-13",
  },
  {
    slug: "why-self-hosting-your-teams-data-matters",
    title: "Why Self-Hosting Your Team's CRM Data Matters More Than It Sounds Like It Should",
    description:
      "Data sovereignty, vendor lock-in, and what happens to your employee records when a SaaS HR vendor gets acquired, breached, or simply raises its price. Why running your own instance changes the calculus.",
    date: "2026-08-13",
  },
];

export type Feature = {
  title: string;
  description: string;
  icon: string;
};

export const features: Feature[] = [
  {
    title: "Telecalling CRM",
    description:
      "A phone-first lead workspace: call, log a single-tap disposition, qualify, move the stage, and schedule the follow-up without leaving the screen. Drag and drop leads across a configurable pipeline — New, Contacted, Connected, Interested, Qualified, Meeting Booked, Proposal Sent, Negotiation, Won — with granular loss reasons on the way out.",
    icon: "phone-call",
  },
  {
    title: "Follow-ups that don't slip",
    description:
      "Real due-dated follow-ups, grouped into overdue, due now, today, tomorrow and upcoming — completed or rescheduled inline from the queue. A 'Call next lead' button picks the single highest-priority lead for you: overdue follow-up first, then a callback due soon, then a hot lead.",
    icon: "calendar-clock",
  },
  {
    title: "Lead scoring you control",
    description:
      "Every lead gets a transparent 0–100 score from budget, decision-maker access, urgency, requirement clarity, timeline and business fit — banded HOT, WARM, or COLD. The weights and thresholds live in your database and are editable in the app, so tuning them never needs a redeploy.",
    icon: "flame",
  },
  {
    title: "Activity timeline",
    description:
      "Every call with its duration and disposition, every note, stage change, reassignment and follow-up, in order, on the lead. Append-only — it records what actually happened rather than what someone remembers.",
    icon: "history",
  },
  {
    title: "Lead assignment & routing",
    description:
      "Three routing modes per rule — round robin, territory, or capacity — each with a response SLA and an overdue queue. Assign or reassign one lead or hundreds at once, with every reassignment written to the audit log.",
    icon: "route",
  },
  {
    title: "Manager & admin dashboards",
    description:
      "Per-telecaller calls, connect rate, leads qualified, meetings booked, follow-ups completed, deals won and revenue — plus an org-wide conversion funnel, leads by stage and source, pipeline value, and follow-up health.",
    icon: "line-chart",
  },
  {
    title: "Deals & forecasting",
    description:
      "Convert a qualified lead into an opportunity without losing its history, then track it on a Kanban deal board with an auditable stage trail, manager-only forecast-category overrides, and a weighted forecast by rep.",
    icon: "trending-up",
  },
  {
    title: "Employee lifecycle",
    description:
      "Add, edit, and remove employees with auto-generated credentials and a unique employee number. Force-reset a password, change a role or department, or reactivate someone from suspension — all rank-guarded, so no one can promote themselves or a peer.",
    icon: "users",
  },
  {
    title: "Role-based access control",
    description:
      "Master Owner, Company Admin, Sales Head, Manager, and Employee form a strict hierarchy enforced server-side on every request — not just a hidden button in the UI.",
    icon: "shield-check",
  },
  {
    title: "Work assignments",
    description:
      "Create work and assign it to a selected employee by number. A guarded status pipeline — Assigned, In Progress, Follow-up, Blocked, Completed, Canceled — rejects invalid jumps and keeps a full transition audit trail with outcome notes.",
    icon: "clipboard-list",
  },
  {
    title: "Attendance tracking",
    description:
      "An explicit shift and break state machine — logged out, working, on break — with server timestamps, one open session per employee, and a dangling break auto-closed on shift end. No guessing who's actually on the clock.",
    icon: "clock",
  },
  {
    title: "Knowledge base & tips",
    description:
      "Upload PDF training documents with a draft/publish lifecycle and per-version acknowledgement tracking, plus a daily 'today's tip' feed on every employee's front page.",
    icon: "book-open",
  },
  {
    title: "Master dashboard",
    description:
      "Live headcount, assignments by status, overdue count, and a real-time 'who's working / on break right now' board — one screen for the whole organization's operational health.",
    icon: "gauge",
  },
  {
    title: "Employee reports",
    description:
      "Login/logout times, total worked and break minutes, per-session breakdown, and assignment counts by outcome — the same evidence for the employee and their manager, not a one-sided surveillance log.",
    icon: "bar-chart-3",
  },
  {
    title: "CSV & Google Sheets",
    description:
      "Export employees and assignments to CSV with formula-injection protection built in. Import employees from CSV with row-level error reporting, and a real Google Sheets adapter that only activates once you provide credentials — never a fake integration.",
    icon: "file-spreadsheet",
  },
  {
    title: "Self-hosted, your data",
    description:
      "Runs entirely on your own server via Docker. No subscription, no per-seat fee, and nothing about your team or customers leaves your infrastructure.",
    icon: "server",
  },
  {
    title: "Backups & one-click updates",
    description:
      "Connect any S3-compatible bucket and Zulivio backs itself up on a schedule — download or restore a copy any time — plus a one-click update button in Settings that pulls the latest release and restarts the app for you.",
    icon: "refresh-cw",
  },
  {
    title: "MCP server for AI assistants",
    description:
      "Point Claude, ChatGPT, or any MCP-compatible client at Zulivio's own MCP server with a personal API key, and it can list your leads, create one, check the sales dashboard, log your attendance, or update a task — scoped to exactly your own role's permissions, nothing more.",
    icon: "bot",
  },
];

export type Screenshot = {
  src: string;
  alt: string;
  caption: string;
};

export const screenshots: Screenshot[] = [
  {
    src: "/screenshots/my-day.webp",
    alt: "Zulivio My Day screen showing calls made today, overdue follow-ups, leads to contact, and hot leads",
    caption: "My Day — the telecaller's queue: who to call, what's due, and what's running hot",
  },
  {
    src: "/screenshots/lead-board.webp",
    alt: "Zulivio lead board with columns for each telecalling stage and draggable lead cards",
    caption: "Lead board — drag a lead across the telecalling pipeline, or move it from its detail page",
  },
  {
    src: "/screenshots/lead-workspace.webp",
    alt: "Zulivio lead workspace with contact details, pipeline stage strip, qualification fields, and activity timeline",
    caption: "Lead workspace — call, qualify, log the outcome, and book the follow-up on one screen",
  },
  {
    src: "/screenshots/disposition.webp",
    alt: "Zulivio call disposition dialog with connected and not-connected outcome options",
    caption: "Call disposition — one tap for the outcome, one for the reason, then back to the queue",
  },
  {
    src: "/screenshots/follow-ups.webp",
    alt: "Zulivio follow-ups queue grouped into overdue, due now, and later today",
    caption: "Follow-ups — grouped by when they're due, completed or rescheduled without leaving the list",
  },
  {
    src: "/screenshots/team-performance.webp",
    alt: "Zulivio telecaller performance comparison chart and per-telecaller table",
    caption: "Telecaller performance — calls, connect rate, meetings, follow-ups, and revenue per rep",
  },
  {
    src: "/screenshots/crm-overview.webp",
    alt: "Zulivio CRM overview with a conversion funnel, pipeline value by stage, and revenue trend",
    caption: "CRM overview — org-wide funnel, pipeline value, revenue trend, and follow-up health",
  },
  {
    src: "/screenshots/pipeline.webp",
    alt: "Zulivio deal pipeline Kanban board",
    caption: "Deal pipeline — qualified leads become opportunities without losing their history",
  },
  {
    src: "/screenshots/sales-dashboard.webp",
    alt: "Zulivio sales dashboard with pipeline value by stage, lead funnel, and forecast by category",
    caption: "Sales dashboard — pipeline value, lead funnel, and weighted forecast by rep",
  },
  {
    src: "/screenshots/dashboard.webp",
    alt: "Zulivio master dashboard showing headcount, assignment status, and live attendance",
    caption: "Master dashboard — headcount, assignments by status, and who's on the clock right now",
  },
  {
    src: "/screenshots/employees.webp",
    alt: "Zulivio employee directory with add, edit, reset password, and remove controls",
    caption: "Employee directory — add, edit, reset password, and remove, all rank-guarded",
  },
  {
    src: "/screenshots/employee-front-page.webp",
    alt: "Zulivio employee front page with shift controls, today's tips, and My Work",
    caption: "Employee front page — shift controls, today's tips, and a personal work queue",
  },
  {
    src: "/screenshots/assignments.webp",
    alt: "Zulivio assignments board with a guarded status pipeline",
    caption: "Assignments — create work, assign it by employee, move it through a guarded pipeline",
  },
  {
    src: "/screenshots/attendance.webp",
    alt: "Zulivio attendance page with shift status and team on-the-clock board",
    caption: "Attendance — your shift, team status, and a link to every employee's full report",
  },
  {
    src: "/screenshots/knowledge.webp",
    alt: "Zulivio knowledge base and tips page with PDF training documents",
    caption: "Knowledge base & tips — PDF training documents and a daily team tip",
  },
  {
    src: "/screenshots/data-hub.webp",
    alt: "Zulivio data hub with CSV and Google Sheets import/export",
    caption: "Data Hub — CSV import/export and a real, credential-gated Google Sheets sync",
  },
  {
    src: "/screenshots/login.webp",
    alt: "Zulivio sign-in page",
    caption: "Sign in — session-based auth with Argon2id password hashing",
  },
];

export type FAQ = { q: string; a: string };

export const faqs: FAQ[] = [
  {
    q: "Is Zulivio really free?",
    a: "Yes. It's open-source software (AGPL-3.0 license) — there's no subscription, no per-seat fee, and no feature locked behind a paid plan. You run it on your own server and it costs nothing beyond the hardware you already have.",
  },
  {
    q: "What can it do today?",
    a: "Two things at once. A telecalling CRM: a lead board with drag-and-drop stages, a single-screen lead workspace, single-tap call dispositions, an activity timeline, due-dated follow-ups, configurable lead scoring, lead assignment and routing rules, deals and forecasting, and manager/admin dashboards. And a workforce-operations core: employee lifecycle management, role-based access control, work assignments with a guarded status pipeline, an attendance shift/break state machine, a PDF knowledge base with daily tips, a live master dashboard, and CSV/Google Sheets import-export.",
  },
  {
    q: "Is this a full sales CRM with leads and pipelines?",
    a: "Yes. Leads move through a configurable telecalling pipeline (New, Contacted, Connected, Interested, Qualified, Meeting Booked, Proposal Sent, Negotiation, Won, plus Lost with a specific reason), a qualified lead converts into an opportunity on a Kanban deal board without losing its history, and managers get conversion, revenue and per-telecaller performance reporting. What isn't built yet — cloud telephony, AI call analysis, marketing automation, and a customer-service workspace — is listed openly on the roadmap rather than implied.",
  },
  {
    q: "Does it replace our dialer or phone system?",
    a: "No, and it doesn't pretend to. Zulivio gives your telecallers a Call button that dials through the phone or softphone they already use, then records the outcome, disposition, duration and next follow-up against the lead. There's no cloud telephony, call recording, or predictive dialer — the integration point for one is built in, but that's a future release, not a claim about today.",
  },
  {
    q: "How does lead scoring work?",
    a: "Each lead scores 0–100 from six signals a telecaller captures on the call: budget available, whether you're speaking to the decision maker, urgency, how clear the requirement is, how soon they'll decide, and business fit. Scores band into HOT, WARM, or COLD. The weight of each signal and the band thresholds are stored per organization and editable in the app — so the rubric matches how your team actually sells, and changing it doesn't need a redeploy.",
  },
  {
    q: "What do I need to run it?",
    a: "A server with Docker installed — your own hardware, a home server, or a cloud VPS. One command (./install.sh) checks for Docker, generates a secure database password, and starts the whole stack.",
  },
  {
    q: "Can I control who sees what?",
    a: "Yes. Roles form a strict hierarchy — Master Owner, Company Admin, Sales Head, Manager, Employee — enforced on the server for every request, not just hidden in the interface. A manager can never promote themselves or a peer to their own rank or above.",
  },
  {
    q: "How does attendance tracking avoid feeling like surveillance?",
    a: "Employees and managers see the same underlying evidence — login/logout times, break duration, assignment outcomes — not a one-sided log the employee can't see. There's no keylogging, no screenshots, no webcam monitoring.",
  },
  {
    q: "Is my organization's data safe if the server restarts?",
    a: "Yes. All data — employees, assignments, attendance, documents — is stored in a PostgreSQL database in a persistent Docker volume, not in a temporary process. Restarting the server or updating the software doesn't touch your saved data.",
  },
  {
    q: "How hard is it to set up?",
    a: "One command downloads and starts everything — it checks for Docker, generates a secure database password, builds the images, and starts the stack. Open the app afterward and create your organization from the setup page. Most people are ready to add their first employee within a few minutes.",
  },
  {
    q: "Can it run on CasaOS or ZimaOS?",
    a: "Yes — a CasaOS/ZimaOS app store listing ships alongside the Docker Compose setup, so it installs the same way any other CasaOS app does.",
  },
  {
    q: "Do I need the internet at all?",
    a: "Only to install the software and pull updates later. Day-to-day use — logging in, managing employees, tracking attendance — needs no external service except the ones you explicitly opt into, like Google Sheets sync.",
  },
  {
    q: "Can I use Zulivio from Claude or another AI assistant?",
    a: "Yes. Zulivio ships its own MCP server — generate a personal API key from Settings > API Keys, point an MCP client at it, and it can list your leads, create one, check the sales dashboard, log your attendance, or update a task for you. Every call is checked against your own role's exact permissions, so it can never do more than you could do yourself in the app.",
  },
];

export type ComparisonRow = {
  feature: string;
  zulivio: boolean | string;
  hosted: boolean | string;
  spreadsheet: boolean | string;
};

export const comparisonRows: ComparisonRow[] = [
  { feature: "Cost",                              zulivio: "Free forever", hosted: "Monthly per-seat fee", spreadsheet: "Free" },
  { feature: "Data stays on your server",          zulivio: true,           hosted: false,                 spreadsheet: true },
  { feature: "Lead pipeline with drag-and-drop stages", zulivio: true,      hosted: true,                  spreadsheet: "Manual" },
  { feature: "Call disposition tracking",          zulivio: true,           hosted: "Paid tier",            spreadsheet: "Manual" },
  { feature: "Due-dated follow-up queue",          zulivio: true,           hosted: "Paid tier",            spreadsheet: "Manual" },
  { feature: "Configurable lead scoring",          zulivio: true,           hosted: "Paid tier",            spreadsheet: false },
  { feature: "Per-telecaller performance reporting", zulivio: true,         hosted: "Paid tier",            spreadsheet: false },
  { feature: "Role-based access control",          zulivio: true,           hosted: "Paid tier",            spreadsheet: false },
  { feature: "Guarded assignment status pipeline", zulivio: true,           hosted: "Paid tier",            spreadsheet: false },
  { feature: "Attendance shift/break tracking",    zulivio: true,           hosted: "Paid add-on",          spreadsheet: "Manual" },
  { feature: "Employee self-service reports",      zulivio: true,           hosted: "Paid tier",            spreadsheet: false },
  { feature: "Knowledge base with version tracking", zulivio: true,         hosted: "Paid add-on",          spreadsheet: false },
  { feature: "CSV import/export",                  zulivio: true,           hosted: true,                  spreadsheet: true },
  { feature: "No vendor lock-in",                  zulivio: true,           hosted: false,                 spreadsheet: true },
  { feature: "Open source",                        zulivio: true,           hosted: false,                 spreadsheet: false },
];
